import { cn } from '@/lib/cn'

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost'
export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  /**
   * Shows a spinner beside the label and blocks interaction while an action runs.
   */
  loading?: boolean
  /** Announced to screen readers while `loading`. Visually the label is unchanged. */
  loadingLabel?: string
  children: React.ReactNode
}

const variantClasses: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-white hover:bg-primary/90 focus-visible:ring-primary',
  secondary:
    'bg-white border border-gray-300 text-gray-800 hover:bg-gray-50 focus-visible:ring-gray-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700',
  danger: 'bg-danger text-white hover:bg-danger/90 focus-visible:ring-danger',
  ghost:
    'bg-transparent text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 focus-visible:ring-gray-400',
}

const sizeClasses: Record<ButtonSize, string> = {
  /** Compact toolbar / table actions (ClickUp-style ~32px) */
  sm: 'h-8 min-h-8 px-2.5 text-xs font-medium',
  md: 'h-8 min-h-8 px-3 text-sm font-medium',
  lg: 'min-h-11 px-5 py-2.5 text-base font-medium',
}

/** Spinner scales with the label it sits next to, matching text optical weight. */
const spinnerSizeClasses: Record<ButtonSize, string> = {
  sm: 'h-3.5 w-3.5 border-[1.5px]',
  md: 'h-4 w-4 border-[1.5px]',
  lg: 'h-[18px] w-[18px] border-2',
}

/**
 * Button with semantic variants and sizes.
 *
 * Pass `loading` while an action is in flight: the button keeps its label visible
 * beside a spinner. Pointer and keyboard activation are blocked for the duration,
 * so callers need not also pass `disabled`.
 */
export const Button = ({
  variant = 'primary',
  size = 'md',
  className,
  disabled,
  loading = false,
  loadingLabel = 'Loading',
  type = 'button',
  children,
  ...rest
}: ButtonProps): React.ReactElement => {
  const isDisabled = disabled === true || loading

  return (
    <button
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      data-loading={loading ? '' : undefined}
      className={cn(
        'relative inline-flex cursor-pointer items-center justify-center rounded-lg font-medium',
        'transition-[background-color,border-color,color,scale,opacity] duration-150',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        'active:scale-[0.96] disabled:pointer-events-none disabled:opacity-50',
        // A loading button is busy, not unavailable: keep it legible rather
        // than fading it to the 50% disabled state.
        loading && 'disabled:opacity-100 active:scale-100',
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      {...rest}
    >
      <span
        className="inline-flex items-center justify-center gap-1.5"
        aria-hidden={loading || undefined}
      >
        {loading ? (
          <span
            className={cn(
              'animate-spin rounded-full border-current border-t-transparent',
              spinnerSizeClasses[size],
            )}
            aria-hidden="true"
          />
        ) : null}
        {children}
      </span>
      {loading ? <span className="sr-only">{loadingLabel}</span> : null}
    </button>
  )
}
